import { Check, ChevronsUpDown, Loader2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ProductType } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useCreateProductType } from '@/features/dictionary';

import { slugify } from '../api/mappers';

const MAX_NAME_LENGTH = 120;

export interface ProductTypeComboboxProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  productTypes: readonly ProductType[];
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * Product type picker with "Add" for a name that isn't there. Unlike a vendor, a product type is a
 * record of its own, so Add creates it at once and picks it; it starts with no attributes, which
 * Catalog setup gives it. A name that only differs by case or punctuation from an existing type
 * would take its slug, so it offers that type instead.
 */
export function ProductTypeCombobox({
  value,
  onChange,
  onBlur,
  productTypes,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: ProductTypeComboboxProps) {
  const { t } = useTranslation('products');
  const message = useErrorMessage();
  const { can } = usePermissions();
  const create = useCreateProductType();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const typed = search.trim();
  const needle = typed.toLowerCase();
  const slug = slugify(typed);
  const isSame = (type: ProductType) =>
    type.name.toLowerCase() === needle || (slug !== '' && type.slug === slug);
  const same = typed ? productTypes.find(isSame) : undefined;
  const rows = typed
    ? productTypes.filter((type) => isSame(type) || type.name.toLowerCase().includes(needle))
    : productTypes;
  // The same name first, so Enter never lands on another type that merely contains it.
  const shown = same ? [same, ...rows.filter((type) => type !== same)] : rows;
  const canCreate = typed !== '' && same === undefined && can('attributes.write');
  const current = productTypes.find((type) => type.id === value);

  const close = () => {
    setOpen(false);
    setSearch('');
    create.reset();
  };
  const choose = (next: string) => {
    onChange(next);
    close();
  };
  const add = () => {
    if (create.isPending) return;
    create.mutate(
      // Nothing left of a name in another alphabet, and the backend wants a slug.
      { name: typed, slug: slug || `type-${crypto.randomUUID().slice(0, 8)}` },
      { onSuccess: (row) => choose(row.id) },
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else {
          close();
          onBlur?.();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={cn('w-full justify-between font-normal', !current && 'text-muted-foreground')}
        >
          <span className="truncate">{current?.name ?? t('typePicker.placeholder')}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t(
              can('attributes.write') ? 'typePicker.searchOrAdd' : 'typePicker.search',
            )}
            value={search}
            onValueChange={(next) => {
              setSearch(next);
              create.reset();
            }}
            maxLength={MAX_NAME_LENGTH}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>{typed ? t('typePicker.noMatch') : t('typePicker.empty')}</CommandEmpty>
            {shown.length > 0 ? (
              <CommandGroup>
                {shown.map((type) => (
                  <CommandItem
                    key={type.id}
                    value={`type:${type.id}`}
                    onSelect={() => choose(type.id)}
                  >
                    <Check
                      className={cn('size-4', type.id === value ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{type.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {canCreate ? (
              <CommandGroup>
                <CommandItem value="create" disabled={create.isPending} onSelect={add}>
                  {create.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Plus className="size-4" aria-hidden />
                  )}
                  <span className="truncate">{t('typePicker.create', { name: typed })}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {value && !typed ? (
              <CommandGroup>
                <CommandItem value="clear" onSelect={() => choose('')}>
                  <X className="size-4" aria-hidden />
                  <span className="text-muted-foreground">{t('fields.noProductType')}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
          {create.isError ? (
            <p role="alert" className="text-destructive border-t px-3 py-2 text-xs">
              {message(create.error)}
            </p>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
